import { Checkbox, Grid, Stack, Typography } from "@mui/material";
import React, { useEffect, useState, useMemo, useCallback, memo } from "react";
import { useSelector } from "react-redux";
import { Postrequestcall } from "../../../../request/Postrequest";
import { FETCH_CLIENT_TIMESHEET_JOBS_AND_ACT_NEW } from "../../../../constant/Apipath";
import { LoadingButton } from "@mui/lab";
import TimesheetSkeletonLoader from "../../shared/components/TimesheetSkeletonLoader";

import { VIEW } from "./ManualTimesheetController";
import { ClientEnglishFallback } from "../../../constant/ClientEnglishFallback";
import { BIGrey } from "../../../../assets/helpers";

function SelectJobsManual({ setView, setSelectJobData, selectedJobData }) {
  const { loginData, selectedClientData, multiLanguageData } = useSelector((state) => state?.main);

  const CLIENT_ID = selectedClientData?.ClientId;
  const CLIENT_COLOR = selectedClientData?.AccentColor || "#000000";
  const CLIENT_EMPLOYEE_ID = selectedClientData?.ClientEmployeeId;
  const TOKEN = loginData?.accessToken;

  const [isLoading, setIsLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [location, setLocation] = useState({ latitude: 0, longitude: 0 });
  const [sortByNearby, setSortByNearby] = useState(false);

  const {
    TEXT_GEOLOCATION_NOT_SUPPORTED,
    TEXT_LOCATION_ACCESS_REQUIRED,
    TEXT_UNABLE_TO_ACCESS_LOCATION,
    TEXT_SELECT_JOB_ACTIVITIES,
    TEXT_SORT_BY,
    TEXT_NEAR_BY,
    TEXT_RECENT,
    BUTTON_NEXT,
    TEXT_NO_JOBS_FOUND
  } = ClientEnglishFallback?.SelectJobsManual(multiLanguageData?.SelectJobsManual || {});

  useEffect(() => {
    fetchLocation();
    fetchJobsAndActivities();
  }, [sortByNearby]);

  const isActivityInSelectedJobData = useCallback(
    (clientJobId, activityId) => {
      if (!selectedJobData || !selectedJobData.length) return false;
      return selectedJobData.some((item) => {
        if (item.clientJobId !== clientJobId) return false;
        const itemActId =
          item.clientJobActivityId ||
          item.ClientJobActivityId ||
          item.clientActivityId ||
          item.activityId ||
          (item.selectionType === "activity" ? item.id : null);
        return itemActId === activityId;
      });
    },
    [selectedJobData]
  );

  const isJobInSelectedJobData = useCallback(
    (clientJobId) => {
      if (!selectedJobData || !selectedJobData.length) return false;
      return selectedJobData.some((item) => {
        if (item.clientJobId !== clientJobId) return false;
        const itemActId =
          item.clientJobActivityId ||
          item.ClientJobActivityId ||
          item.clientActivityId ||
          item.activityId;
        return (!itemActId || itemActId === 0) && (item.selectionType === "job" || !item.selectionType || !item.id);
      });
    },
    [selectedJobData]
  );

  const mapSelectedStatus = useCallback(
    (jobsData) => {
      return jobsData.map((job) => {
        const hasActivities = job.clientJobActivitiesList?.length > 0;

        if (!hasActivities) {
          const isSelected = isJobInSelectedJobData(job.clientJobId);
          return { ...job, isSelected };
        }

        const updatedActivities = job.clientJobActivitiesList.map((activity) => {
          const actId = activity.id || activity.clientActivityId;
          const isSelected = isActivityInSelectedJobData(job.clientJobId, actId);
          return {
            ...activity,
            isSelected
          };
        });

        const anyActivitySelected = updatedActivities.some((act) => act.isSelected);

        return {
          ...job,
          isSelected: anyActivitySelected,
          clientJobActivitiesList: updatedActivities
        };
      });
    },
    [isJobInSelectedJobData, isActivityInSelectedJobData]
  );

  const fetchLocation = async () => {
    if (!navigator.permissions || !navigator.geolocation) {
      console.warn("fetchLocation-> Geolocation or Permissions API not supported");
      return;
    }

    try {
      const permissionStatus = await navigator.permissions.query({ name: "geolocation" });
      if (permissionStatus.state === "granted") {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setLocation({
              latitude: position.coords.latitude,
              longitude: position.coords.longitude
            });
          },
          (err) => {
            console.warn("fetchLocation-> Location access denied or unavailable", err);
          }
        );
      }
    } catch (err) {
      console.error("fetchLocation-> Error accessing geolocation:", err);
    }
  };

  const fetchJobsAndActivities = async () => {
    setIsLoading(true);

    let userLatitude = 0;
    let userLongitude = 0;

    if (sortByNearby) {
      if (location?.latitude && location?.longitude) {
        userLatitude = location.latitude;
        userLongitude = location.longitude;
      } else {
        if (!navigator.permissions || !navigator.geolocation) {
          alert(TEXT_GEOLOCATION_NOT_SUPPORTED);
          setIsLoading(false);
          return;
        }

        try {
          const permissionStatus = await navigator.permissions.query({ name: "geolocation" });

          if (permissionStatus.state === "denied") {
            alert(TEXT_LOCATION_ACCESS_REQUIRED);
            setIsLoading(false);
            return;
          }

          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject);
          });

          userLatitude = position.coords.latitude;
          userLongitude = position.coords.longitude;

          setLocation({
            latitude: userLatitude,
            longitude: userLongitude
          });
        } catch (error) {
          alert(TEXT_UNABLE_TO_ACCESS_LOCATION);
          setIsLoading(false);
          return;
        }
      }
    }

    try {
      const payload = {
        PageNumber: 1,
        NrRowsPerPage: 100,
        UserLatitude: userLatitude,
        UserLongitude: userLongitude,
        ClientId: CLIENT_ID,
        ClientEmployeeId: CLIENT_EMPLOYEE_ID,
        SortBy: sortByNearby ? 1 : 2
      };

      const response = await Postrequestcall(FETCH_CLIENT_TIMESHEET_JOBS_AND_ACT_NEW, payload, TOKEN);

      const mappedData = mapSelectedStatus(response.data.data || []);
      setJobs(mappedData);
    } catch (error) {
      console.error("fetchJobsAndActivities-> Error fetching jobs and activities:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleJobClick = useCallback((job, index) => {
    setJobs((prevJobs) =>
      prevJobs.map((item, i) => (i === index ? { ...item, isSelected: !item.isSelected } : item))
    );
  }, []);

  const handleActivityClick = useCallback((job, jindex, aindex) => {
    setJobs((prevJobs) => {
      return prevJobs.map((item, i) => {
        if (i === jindex) {
          const updatedActivities = item.clientJobActivitiesList.map((act, actIndex) =>
            actIndex === aindex ? { ...act, isSelected: !act.isSelected } : act
          );

          const anyActivitySelected = updatedActivities.some((act) => act.isSelected);

          return {
            ...item,
            isSelected: anyActivitySelected,
            clientJobActivitiesList: updatedActivities
          };
        }
        return item;
      });
    });
  }, []);

  const formatData = useCallback(() => {
    try {
      const selected = [];

      jobs.forEach((job) => {
        const hasActivities = job.clientJobActivitiesList?.length > 0;
        if (!hasActivities && job.isSelected) {
          selected.push({
            ...job,
            selectionType: "job"
          });
        }

        if (hasActivities) {
          job.clientJobActivitiesList.forEach((activity) => {
            if (activity.isSelected) {
              selected.push({
                ...activity,
                selectionType: "activity",
                clientJobId: job.clientJobId,
                clientJobName: job.clientJobName,
                clientJobCode: job.clientJobCode
              });
            }
          });
        }
      });

      const updatedJobData = selected.map((selectedItem) => {
        const existing = selectedJobData.find((item) => {
          if (item.clientJobId !== selectedItem.clientJobId) return false;
          if (selectedItem.selectionType === "activity") {
            const selectedActId = selectedItem.id || selectedItem.clientActivityId;
            const itemActId =
              item.clientJobActivityId ||
              item.ClientJobActivityId ||
              item.clientActivityId ||
              item.activityId ||
              (item.selectionType === "activity" ? item.id : null);
            return itemActId === selectedActId;
          } else {
            const itemActId =
              item.clientJobActivityId ||
              item.ClientJobActivityId ||
              item.clientActivityId ||
              item.activityId;
            return (!itemActId || itemActId === 0) && (item.selectionType === "job" || !item.selectionType || !item.id);
          }
        });

        return (
          existing || {
            ...selectedItem,
            isSelected: true,
            hours: "",
            mileage: "",
            mileageUnit: "km",
            notes: "",
            activityId: selectedItem.selectionType === "activity" ? (selectedItem.id || selectedItem.clientActivityId) : 0,
            id: selectedItem.selectionType === "activity" ? (selectedItem.id || selectedItem.clientActivityId) : 0,
            timesheetJobId: selectedItem.timesheetJobId || 0
          }
        );
      });

      setSelectJobData(updatedJobData);
      setView(VIEW.LISTING);
    } catch (error) {
      console.error("formatData-> Error formatting data:", error);
    }
  }, [jobs, selectedJobData, setSelectJobData, setView]);

  const hasSelectedJobs = useMemo(() => {
    return jobs.some(
      (job) =>
        (job.clientJobActivitiesList?.length === 0 && job.isSelected) ||
        job.clientJobActivitiesList?.some((act) => act.isSelected)
    );
  }, [jobs]);

  return (
    <>
      {isLoading ? (
        <TimesheetSkeletonLoader variant="selectJob" />
      ) : (
        <Grid container spacing={1}>
          <Grid size={12}>
            <Stack direction="row" sx={{ justifyContent: "space-between" }}>
              <Typography>{TEXT_SELECT_JOB_ACTIVITIES}</Typography>
              <Stack sx={{ gap: 0.5 }} direction="row" >
                <Typography>{TEXT_SORT_BY}</Typography>
                <Typography
                  onClick={() => setSortByNearby((prev) => !prev)}
                  sx={{ color: CLIENT_COLOR, cursor: "pointer" }}>
                  {sortByNearby ? TEXT_NEAR_BY : TEXT_RECENT}
                </Typography>
              </Stack>
            </Stack>
          </Grid>
          <Grid
            sx={{
              maxHeight: "300px",
              overflow: "auto"
            }}
            size={12}>
            <Stack spacing={1}>
              {jobs?.length === 0 && (
                <Stack sx={{ justifyContent: "center", alignItems: "center", height: "100px", width: "100%" }}>
                  <Typography sx={{ color: BIGrey }}>{TEXT_NO_JOBS_FOUND}</Typography>
                </Stack>
              )}
              {jobs.map((job, index) => (
                <Listing
                  key={`job-${job.clientJobId || index}`}
                  jindex={index}
                  job={job}
                  CLIENT_COLOR={CLIENT_COLOR}
                  handleJobClick={handleJobClick}
                  handleActivityClick={handleActivityClick}
                />
              ))}
            </Stack>
          </Grid>

          <Grid size={12}>
            <LoadingButton
              disabled={!hasSelectedJobs}
              onClick={formatData}
              loadingPosition="start"
              startIcon={<></>}
              fullWidth
              sx={{
                bgcolor: !hasSelectedJobs ? "#e4e4e4" : CLIENT_COLOR,
                color: "white",
                textTransform: "none"
              }}>
              {BUTTON_NEXT}
            </LoadingButton>
          </Grid>
        </Grid>
      )}
    </>
  );
}

const Listing = memo(({ job, jindex, CLIENT_COLOR, handleJobClick, handleActivityClick }) => {
  const hasActivities = job.clientJobActivitiesList?.length > 0;

  return (
    <Stack
      onClick={() => {
        if (!hasActivities) {
          handleJobClick(job, jindex);
        }
      }}
      sx={{
        border: "1px solid #e4e4e4",
        borderColor: !hasActivities ? "#e4e4e4" : "transparent",
        borderRadius: "5px",
        cursor: !hasActivities ? "pointer" : "default"
      }}
      spacing={1}>
      <Stack direction="row" sx={{ alignItems: "center" }}>
        {!hasActivities && (
          <>
            <Checkbox
              checked={!!job.isSelected}
              sx={{
                "&.Mui-checked": {
                  color: CLIENT_COLOR
                }
              }}
            />
            <Typography sx={{ fontWeight: 600 }}>
              {job.clientJobCode} - {job.clientJobName}
            </Typography>
          </>
        )}
      </Stack>

      {hasActivities && (
        <Stack sx={{ gap: 1 }} >
          <Typography>
            {job.clientJobCode} - {job.clientJobName}
          </Typography>
          <Stack sx={{ paddingLeft: "15px", gap: 1 }}>
            {job.clientJobActivitiesList.map((act, aindex) => (
              <Stack
                key={`${job.clientJobId}-${act.id || aindex}`}
                direction="row"
                onClick={(e) => {
                  handleActivityClick(job, jindex, aindex);
                }}
                sx={{
                  border: "1px solid #e4e4e4",
                  borderRadius: "5px",
                  cursor: "pointer",
                  alignItems: "center"
                }}>
                <Checkbox
                  checked={!!act.isSelected}
                  sx={{
                    "&.Mui-checked": {
                      color: CLIENT_COLOR
                    }
                  }}
                />
                <Typography sx={{ fontWeight: 600 }}>
                  {act.labourActivityCode} - {act.labourActivityName}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
});

export default SelectJobsManual;

